import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import axios from "axios";
import {
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  useDroppable,
} from "@dnd-kit/core";
import { ModelElementType } from "components/entities";
import {
  isDeletionAction,
  TableAction,
  TableDeletionAction,
  TableDeletionActionType,
} from "./DeleteModelDialogTypes";
import {
  fetchModelDeletionAnalysis,
  transferTableOwnership,
} from "./DeletionServices";
import { fetchModels } from "@/components/main/utils/models/ModelServices";
import { catalogRowToModelElement } from "@/components/main/utils/models/types";
import { fetchCatalogModels } from "@/lib/api/catalog";
import {
  ModelDeletionAnalysis,
  TableDeletionInfo,
  TrashModel,
} from "target_wasm";
import { showNotificationGlobal } from "stores/useNotificationStore";

interface TransferState {
  activeId: string | null;
  batchIds: Set<string>;
  successIds: Set<string>;
  failedIds: Set<string>;
  fadingIds: Set<string>;
  removedIds: Set<string>;
}

interface DragState {
  activeId: string | null;
  overModelId: string | null;
  isOverTrash: boolean;
  isOverTransferSection: boolean;
}

export const useDeleteModel = (
  isOpen: boolean,
  currentModel: ModelElementType | null,
  onDeleteModel: (
    actions: Record<string, TableDeletionAction>,
  ) => Promise<void>,
  onClose: () => void,
  initialAvailableModels?: ModelElementType[],
  trashModels?: TrashModel[],
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDeletingModel, setIsDeletingModel] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [modelSearchQuery, setModelSearchQuery] = useState("");
  const [expandedSections, setExpandedSections] = useState({
    critical: true,
    moderate: true,
    low: true,
  });
  const [analysis, setAnalysis] = useState<Pick<
    ModelDeletionAnalysis,
    "tables_js" | "owned_models_js"
  > | null>(null);
  const [availableModels, setAvailableModels] = useState<ModelElementType[]>(
    [],
  );
  const [stagedActions, setStagedActions] = useState<
    Record<string, TableAction>
  >({});

  const [selectedTableIds, setSelectedTableIds] = useState<Set<string>>(
    new Set(),
  );
  const [dragState, setDragState] = useState<DragState>({
    activeId: null,
    overModelId: null,
    isOverTrash: false,
    isOverTransferSection: false,
  });
  const dragStartSelectedTablesRef = useRef<Set<string>>(new Set());
  const lastSelectedTableRef = useRef<string | null>(null);
  const baseSelectionRef = useRef<Set<string>>(new Set());

  const [transferState, setTransferState] = useState<TransferState>({
    activeId: null,
    batchIds: new Set(),
    successIds: new Set(),
    failedIds: new Set(),
    fadingIds: new Set(),
    removedIds: new Set(),
  });

  const updateSet = useCallback(
    (
      key: keyof TransferState | "selectedTableIds",
      operation: "add" | "delete" | "set" | "toggle",
      values: string | string[] | Set<string>,
    ) => {
      const normalize = (v: string | Set<string>) => String(v);
      const toArray = (v: string | string[] | Set<string>) =>
        v instanceof Set ? Array.from(v) : Array.isArray(v) ? v : [v];

      const updater = (prev: Set<string>) => {
        if (operation === "set") {
          return new Set(toArray(values).map(normalize));
        }
        const next = new Set(prev);
        toArray(values).forEach((item) => {
          const normalized = normalize(item);
          if (operation === "toggle") {
            if (next.has(normalized)) {
              next.delete(normalized);
            } else {
              next.add(normalized);
            }
          } else if (operation === "add") {
            next.add(normalized);
          } else {
            next.delete(normalized);
          }
        });
        return next;
      };

      if (key === "selectedTableIds") {
        setSelectedTableIds(updater);
      } else if (key === "activeId") {
        setTransferState((prev) => ({
          ...prev,
          [key]: values ? String(values) : null,
        }));
      } else {
        setTransferState((prev) => ({
          ...prev,
          [key]: updater(prev[key as keyof TransferState] as Set<string>),
        }));
      }
    },
    [],
  );

  const handleStageAction = useCallback(
    (tableId: string, action: TableAction | undefined) => {
      setStagedActions((prev) => {
        const next = { ...prev };
        if (action === undefined) delete next[tableId];
        else next[tableId] = action;
        return next;
      });
    },
    [],
  );

  // A deleted space's review has no single model: its tables come from several.
  const subjectId =
    currentModel?.model_id ?? trashModels?.[0]?.model_id_js ?? null;
  const sourceModelOf = (tableId: string) =>
    trashModels?.find((m) => m.tables_js.some((t) => t.table_id_js === tableId))
      ?.model_id_js ?? currentModel?.model_id;

  useEffect(() => {
    if (isOpen && subjectId) {
      initializeData();
    } else {
      resetState();
    }
  }, [isOpen, subjectId]);

  const resetState = () => {
    setIsLoading(false);
    setGlobalError(null);
    setIsDeletingModel(false);
    setStagedActions({});
    setSelectedTableIds(new Set());
    lastSelectedTableRef.current = null;
    baseSelectionRef.current = new Set();
    setAnalysis(null);
    setTransferState({
      activeId: null,
      batchIds: new Set(),
      successIds: new Set(),
      failedIds: new Set(),
      fadingIds: new Set(),
      removedIds: new Set(),
    });
  };

  const initializeData = async () => {
    if (!subjectId) return;
    setIsLoading(true);
    setGlobalError(null);

    // A catalog table only moves to another catalog model, and nobody owns one.
    const loadTargets = (): Promise<ModelElementType[]> =>
      initialAvailableModels?.length
        ? Promise.resolve(initialAvailableModels)
        : currentModel?.is_catalog
          ? fetchCatalogModels()
              .then(({ models }) =>
                models
                  .filter((m) => !m.scheduled_deletion_date)
                  .map(catalogRowToModelElement),
              )
              .catch(() => [])
          : fetchModels(true).catch(() => []);

    try {
      const [analysisData, modelsData] = trashModels
        ? [
            {
              tables_js: trashModels.flatMap((m) => m.tables_js),
              owned_models_js: [],
            },
            await loadTargets(),
          ]
        : await Promise.all([
            fetchModelDeletionAnalysis(subjectId),
            loadTargets(),
          ]);

      const initialActions: Record<string, TableDeletionAction> = {};
      (analysisData.tables_js ?? []).forEach((table: TableDeletionInfo) => {
        const deletionState = (
          table as TableDeletionInfo & { deletion_state_js?: string }
        ).deletion_state_js;
        if (trashModels || deletionState === "ScheduledHardDeletion") {
          initialActions[table.table_id_js] = {
            type: TableDeletionActionType.PendingDeletion,
          };
        }
      });

      setAnalysis(analysisData);
      setAvailableModels(modelsData);
      setStagedActions(initialActions);
    } catch (err: unknown) {
      console.error(err);
      if (axios.isAxiosError(err)) {
        setGlobalError(
          err.response?.data || "Failed to load table information",
        );
      } else {
        setGlobalError("Failed to load table information");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const finalizeRemoval = (tableId: string) => {
    handleStageAction(tableId, undefined);
    updateSet("removedIds", "add", tableId);
    (["successIds", "fadingIds", "batchIds"] as const).forEach((key) =>
      updateSet(key, "delete", tableId),
    );
  };
  const handleTransferLifecycle = async (
    tableId: string,
    targetModelId: string,
    isBatch = false,
  ): Promise<boolean> => {
    const id = String(tableId);
    const sourceModelId = sourceModelOf(id);
    if (!sourceModelId) return false;

    if (!isBatch) {
      updateSet("failedIds", "delete", id);
      setTransferState((prev) => ({ ...prev, activeId: id }));
    }

    try {
      const result = await transferTableOwnership(
        id,
        sourceModelId,
        targetModelId,
      );

      if (!result.success) throw new Error(result.message);

      updateSet("successIds", "add", id);
      updateSet("selectedTableIds", "delete", id);

      if (!isBatch) {
        setTransferState((prev) => ({ ...prev, activeId: null }));
      } else {
        updateSet("batchIds", "delete", id);
      }

      setTimeout(() => {
        updateSet("fadingIds", "add", id);
        setTimeout(() => finalizeRemoval(id), 500);
      }, 2000);

      return true;
    } catch (err) {
      console.error(err);
      if (!isBatch) {
        setTransferState((prev) => ({ ...prev, activeId: null }));
        const table = analysis?.tables_js?.find((t) => t.table_id_js === id);
        const tableName = table?.table_name_js || "table";
        showNotificationGlobal(
          `Failed to transfer table "${tableName}"`,
          "error",
        );
      } else {
        updateSet("batchIds", "delete", id);
      }
      updateSet("failedIds", "add", id);
      setTimeout(() => updateSet("failedIds", "delete", id), 3000);
      return false;
    }
  };

  const executeBatchTransfer = async (
    targetModelId: string,
    tables: TableDeletionInfo[],
  ) => {
    if (!subjectId || !tables.length) return;
    setGlobalError(null);
    const tableIds = tables.map((t) => String(t.table_id_js));
    updateSet("failedIds", "delete", tableIds);
    updateSet("successIds", "delete", tableIds);
    updateSet("batchIds", "set", new Set(tableIds));
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => setTimeout(resolve, 50)),
    );
    const results: boolean[] = [];
    for (const table of tables) {
      const success = await handleTransferLifecycle(
        table.table_id_js,
        targetModelId,
        true,
      );
      results.push(success);
    }
    const successCount = results.filter((r) => r).length;
    const failureCount = results.length - successCount;
    if (failureCount > 0) {
      if (failureCount === tables.length) {
        showNotificationGlobal(
          `Failed to transfer ${tables.length} table${tables.length !== 1 ? "s" : ""}`,
          "error",
        );
      } else {
        showNotificationGlobal(
          `Failed to transfer ${failureCount} of ${tables.length} table${tables.length !== 1 ? "s" : ""}`,
          "error",
        );
      }
    }
  };

  const handleDeleteModel = async () => {
    if (!currentModel) return;
    const finalActions = Object.entries(stagedActions).reduce(
      (acc, [id, action]) => {
        if (isDeletionAction(action))
          acc[id] = {
            type: TableDeletionActionType.ScheduleDeletion,
          };
        return acc;
      },
      {} as Record<string, TableDeletionAction>,
    );
    setIsDeletingModel(true);
    try {
      await onDeleteModel(finalActions);
      onClose();
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        setGlobalError(err.response?.data || "Failed to delete model");
      } else {
        setGlobalError("Failed to delete model");
      }
      setIsDeletingModel(false);
    }
  };

  const computeDragState = (overId: string): Partial<DragState> => {
    if (overId.startsWith("model-"))
      return {
        overModelId: overId.replace("model-", ""),
        isOverTrash: false,
        isOverTransferSection: true,
      };
    if (overId === "trash")
      return {
        overModelId: null,
        isOverTrash: true,
        isOverTransferSection: false,
      };
    if (overId === "transfer-section")
      return {
        overModelId: null,
        isOverTrash: false,
        isOverTransferSection: true,
      };
    return {
      overModelId: null,
      isOverTrash: false,
      isOverTransferSection: false,
    };
  };

  const dndHandlers = {
    onDragStart: (event: DragStartEvent) => {
      const id = String(event.active.id);
      setDragState((prev) => ({ ...prev, activeId: id }));
      const currentSelection = selectedTableIds.has(id)
        ? new Set(selectedTableIds)
        : new Set([id]);
      dragStartSelectedTablesRef.current = currentSelection;
      if (!selectedTableIds.has(id)) {
        updateSet("selectedTableIds", "set", new Set([id]));
      }
    },
    onDragOver: (event: DragOverEvent) => {
      setDragState((prev) => ({
        ...prev,
        ...computeDragState(String(event.over?.id || "")),
      }));
    },
    onDragCancel: () => {
      setDragState({
        activeId: null,
        overModelId: null,
        isOverTrash: false,
        isOverTransferSection: false,
      });
      dragStartSelectedTablesRef.current = new Set();
    },
    onDragEnd: (event: DragEndEvent) => {
      const { over } = event;
      const tablesToProcess = Array.from(dragStartSelectedTablesRef.current);

      if (over && subjectId) {
        const dropId = String(over.id);

        if (dropId.startsWith("model-")) {
          const targetModelId = dropId.replace("model-", "");
          tablesToProcess.forEach((id) =>
            handleStageAction(id, {
              type: "transfer",
              target_model_id: targetModelId,
            }),
          );
          updateSet("selectedTableIds", "set", new Set());
        } else if (dropId === "trash") {
          tablesToProcess.forEach((id) => {
            handleStageAction(id, {
              type: TableDeletionActionType.PendingDeletion,
            });
          });
          updateSet("selectedTableIds", "set", new Set());
        }
      }

      setDragState({
        activeId: null,
        overModelId: null,
        isOverTrash: false,
        isOverTransferSection: false,
      });
      dragStartSelectedTablesRef.current = new Set();
    },
  };

  const { setNodeRef: transferSectionRef } = useDroppable({
    id: "transfer-section",
  });

  const filteredModels = useMemo(() => {
    let models = availableModels.length
      ? availableModels
      : initialAvailableModels || [];
    if (currentModel)
      models = models.filter((m) => m.model_id !== currentModel.model_id);
    const ownedModels = analysis?.owned_models_js;
    if (ownedModels && ownedModels.length > 0) {
      const ownedIds = new Set(ownedModels.map((m: unknown) => String(m)));
      models = models.filter((m) => ownedIds.has(m.model_id));
    }
    if (modelSearchQuery.trim())
      models = models.filter((m) =>
        m.name.toLowerCase().includes(modelSearchQuery.toLowerCase()),
      );
    return models;
  }, [
    availableModels,
    initialAvailableModels,
    currentModel,
    analysis,
    modelSearchQuery,
  ]);

  const handleTableClick = useCallback(
    (tableId: string, event: React.MouseEvent, orderedTableIds: string[]) => {
      if (event.shiftKey && lastSelectedTableRef.current) {
        const lastIndex = orderedTableIds.indexOf(lastSelectedTableRef.current);
        const currentIndex = orderedTableIds.indexOf(tableId);

        if (lastIndex !== -1 && currentIndex !== -1) {
          const start = Math.min(lastIndex, currentIndex);
          const end = Math.max(lastIndex, currentIndex);
          const rangeIds = orderedTableIds.slice(start, end + 1);
          setSelectedTableIds(
            new Set([...baseSelectionRef.current, ...rangeIds]),
          );
          return;
        }
      }
      // Snapshot current selection as base, then toggle
      const newBase = new Set(selectedTableIds);
      if (newBase.has(tableId)) {
        newBase.delete(tableId);
      } else {
        newBase.add(tableId);
      }
      baseSelectionRef.current = newBase;
      setSelectedTableIds(new Set(newBase));
      lastSelectedTableRef.current = tableId;
    },
    [selectedTableIds],
  );

  const canDelete = useMemo(() => {
    if (!analysis?.tables_js?.length) return true;
    return analysis.tables_js.every((t) => {
      const id = t.table_id_js;
      return (
        transferState.removedIds.has(id) || isDeletionAction(stagedActions[id])
      );
    });
  }, [analysis, transferState.removedIds, stagedActions]);

  return {
    loading: isLoading,
    deleting: isDeletingModel,
    error: globalError,
    analysis,
    stagedActions,
    selectedTables: selectedTableIds,
    expandedSections,
    modelSearchQuery,
    ...transferState,
    totalTables: analysis?.tables_js?.length ?? 0,
    canDelete,
    availableModels: filteredModels,
    handleActionChange: handleStageAction,
    executeTransfer: (id: string, target: string) =>
      handleTransferLifecycle(id, target),
    executeBatchTransfer,
    executeDelete: handleDeleteModel,
    handleCancel: onClose,
    handleTableRemoved: (id: string) => handleStageAction(id, undefined),
    setModelSearchQuery,
    toggleSection: (section: keyof typeof expandedSections) =>
      setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] })),
    handleTableClick,
    selectAllTables: (tables: TableDeletionInfo[]) => {
      const allSelected = tables.every((t) =>
        selectedTableIds.has(t.table_id_js),
      );
      updateSet(
        "selectedTableIds",
        allSelected ? "delete" : "add",
        tables.map((t) => t.table_id_js),
      );
    },
    dndHandlers,
    dragState,
    transferSectionRef,
    getUnassignedTables: (tables: TableDeletionInfo[]) =>
      tables.filter((t) => {
        const id = t.table_id_js;
        return !(
          transferState.removedIds.has(id) ||
          transferState.activeId === id ||
          transferState.successIds.has(id) ||
          stagedActions[id]
        );
      }),
    getAssignedTablesForModel: (modelId: string) =>
      (analysis?.tables_js ?? []).filter((t) => {
        const action = stagedActions[t.table_id_js];
        return (
          !transferState.removedIds.has(t.table_id_js) &&
          action?.type === "transfer" &&
          action.target_model_id === modelId
        );
      }),
  };
};
